/**
 * NFR-05: every failure state is designed.
 *
 * Note what this deliberately does NOT do: report the error anywhere. Error
 * reporting is a third-party network request carrying, in this app, a stack
 * trace from a health record (L-01, NFR-11, R-01). There is no Sentry here and
 * there will not be one.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { t } from '@copy/index'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Local only. Never transmitted.
    if (import.meta.env.DEV) console.error(error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children
    return (
      <main className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p>{t('common.error')}</p>
        <button
          type="button"
          className="rounded-lg bg-accent px-4 py-2 text-accent-ink transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
          onClick={() => this.setState({ failed: false })}
        >
          {t('common.retry')}
        </button>
        <p className="text-xs text-ink-muted">{t('privacy.localOnly')}</p>
      </main>
    )
  }
}
